import React, { useState, useEffect, useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { BASE_URL } from "../utils/url.js";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DetailedForm from "./detailedForm.jsx";

const Form = ({ selectedVehicle, onClose }) => {
  const [locationFrom, setLocationFrom] = useState("");
  const [locationTo, setLocationTo] = useState("");
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateTime, setDateTime] = useState(new Date());
  const [price, setPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState(null);
  const [showDetailedForm, setShowDetailedForm] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const now = new Date();

  const vehiclePriceMap = useMemo(() => ({
    "Executive Car": "executive_price",
    "Saloon Car": "saloon_price",
    "MPV Car": "mpv_price",
    "8 Seater": "eight_seater_price"
  }), []);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BASE_URL}/api/location`);
        if (!response.ok) throw new Error('Failed to fetch locations');
        const data = await response.json();
         console.log('API Response:', data);
        if (data.error) throw new Error(data.message);
        setLocations(data.data);
      } catch (err) {
        setError(err.message);
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchLocations();
  }, []);

  useEffect(() => {
    const fetchPricing = async () => {
      if (locationFrom && locationTo && selectedVehicle) {
        try {
          setPriceLoading(true);
          setPriceError(null);
          const response = await fetch(
            `${BASE_URL}/api/pricings/price/by-locations?fromLocationId=${locationFrom}&toLocationId=${locationTo}`
          );
          if (!response.ok) throw new Error('Failed to fetch pricing');
          const data = await response.json();
          if (!data.success) throw new Error(data.message || 'Pricing not found');

          const priceField = vehiclePriceMap[selectedVehicle.title];
          if (!priceField) throw new Error('Pricing not available for selected vehicle');

          setPrice({
            basePrice: data.data[priceField],
            surcharge: data.data.surcharge || 0,
            surchargeDesc: data.data.surcharge_desc || '',
            total: data.data[priceField] + (data.data.surcharge || 0)
          });
        } catch (err) {
          setPrice(null);
          setPriceError(err.message);
          toast.error(`Pricing error: ${err.message}`);
        } finally {
          setPriceLoading(false);
        }
      } else {
        setPrice(null);
      }
    };
    fetchPricing();
  }, [locationFrom, locationTo, selectedVehicle, vehiclePriceMap]);

  const handleBookNow = () => {
    if (!locationFrom || !locationTo) {
      toast.error("Please select both locations");
      return;
    }
    if (!price) {
      toast.error("Please wait while we fetch pricing information");
      return;
    }

    const fromLocation = locations.find(loc => loc.id.toString() === locationFrom);
    const toLocation = locations.find(loc => loc.id.toString() === locationTo);

    const formattedDate = dateTime.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    const bookingDetails = {
      selectedVehicle,
      locationFrom: {
        id: fromLocation.id,
        name: fromLocation.name,
        details: `${fromLocation.houseName} ${fromLocation.streetName1}, ${fromLocation.town}, ${fromLocation.post_code}`
      },
      locationTo: {
        id: toLocation.id,
        name: toLocation.name,
        details: `${toLocation.houseName} ${toLocation.streetName1}, ${toLocation.town}, ${toLocation.post_code}`
      },
      dateTime: formattedDate,
      rawDateTime: dateTime,
      priceDetails: price
    };

    setBookingData(bookingDetails);
    setShowDetailedForm(true);
  };

  // Handle date selection (keep picker OPEN)
  const handleDateSelect = (date) => {
    if (date) {
      const newDateTime = new Date(date);
      // Preserve current time if possible
      newDateTime.setHours(dateTime.getHours(), dateTime.getMinutes(), 0, 0);
      setDateTime(newDateTime);
      // Do NOT close the picker
    }
  };

  // Handle time selection (CLOSE picker)
  const handleTimeSelect = (hours, minutes) => {
    const newDateTime = new Date(dateTime);
    newDateTime.setHours(hours, minutes, 0, 0);
    setDateTime(newDateTime);
    setIsDatePickerOpen(false); // ✅ Close only on time select
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center p-8">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500 mb-4"></div>
      <p className="text-white text-lg">Loading locations...</p>
    </div>
  );

  if (error) return (
    <div className="p-6 text-center">
      <div className="bg-red-500/20 p-4 rounded-lg border border-red-500">
        <p className="text-red-300 font-medium">Error: {error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="mt-3 px-4 py-2 bg-red-600 hover:bg-red-700 rounded text-white transition"
        >
          Try Again
        </button>
      </div>
    </div>
  );

  return (
    <div className="relative p-1 overflow-y-auto">
      <ToastContainer position="top-center" autoClose={3000} />

      {/* Detailed Form Popup */}
      {showDetailedForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75" 
              aria-hidden="true"
              onClick={() => setShowDetailedForm(false)}
            />
            <div className="inline-block align-bottom bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <DetailedForm 
                  bookingData={bookingData} 
                  onClose={() => setShowDetailedForm(false)}
                  locations={locations}
                  onBookingSuccess={onClose}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-5">
        {/* Location From */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Pickup Location</label>
          <select
            value={locationFrom}
            onChange={(e) => setLocationFrom(e.target.value)}
            disabled={loading}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
          >
            <option value="">Select pickup location</option>
            {locations.map((location) => (
              <option key={location.id} value={location.id.toString()} className="bg-gray-800">
                {location.name}
              </option>
            ))}
          </select>
        </div>

        {/* Location To */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Drop-off Location</label>
          <select
            value={locationTo}
            onChange={(e) => setLocationTo(e.target.value)}
            disabled={loading}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
          >
            <option value="">Select drop-off location</option>
            {locations
              .filter(loc => loc.id.toString() !== locationFrom)
              .map((location) => (
                <option key={location.id} value={location.id.toString()} className="bg-gray-800">
                  {location.name}
                </option>
              ))}
          </select>
        </div>

        {/* Date & Time Picker — Updated Behavior */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Date & Time</label>
          <div className="relative">
            <input
              type="text"
              readOnly
              value={
                dateTime
                  ? new Intl.DateTimeFormat('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    }).format(dateTime)
                  : ''
              }
              onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
              placeholder="Select date & time"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition cursor-pointer"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>

          {isDatePickerOpen && (
            <div className="mt-3 p-4 bg-gray-800 border border-gray-700 rounded-lg">
              <div className="flex flex-col md:flex-row gap-6">
                {/* Calendar */}
                <div className="flex-1">
                  <DatePicker
                    selected={dateTime}
                    onChange={handleDateSelect} // ✅ Only updates date, stays open
                    inline
                    minDate={now}
                    calendarClassName="!bg-gray-800 !border-0 !text-white"
                    dayClassName={(date) => {
                      const isPast = date < now.setHours(0, 0, 0, 0);
                      const isToday = date.toDateString() === new Date().toDateString();
                      const isSelected = isSameDay(date, dateTime);
                      const isDisabled = isPast || (isToday && date < now);

                      if (isDisabled) return "!text-gray-600 !bg-gray-900 cursor-not-allowed";
                      if (isSelected) return "!bg-yellow-500 !text-gray-900 !font-bold rounded-full";
                      return "!text-white hover:!bg-gray-700 rounded-full";
                    }}
                  />
                </div>

                {/* Time List */}
                <div className="w-full md:w-48">
                  <label className="block text-xs font-medium text-gray-400 mb-2 text-center">Select Time</label>
                  <div className="max-h-60 overflow-y-auto pr-1 custom-time-scrollbar">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {(() => {
                        const times = [];
                        const today = new Date().toDateString();
                        const selectedDateIsToday = dateTime.toDateString() === today;

                        for (let h = 0; h < 24; h++) {
                          for (let m = 0; m < 60; m += 15) {
                            const isDisabled =
                              selectedDateIsToday &&
                              (h < now.getHours() || (h === now.getHours() && m < now.getMinutes()));

                            const timeStr = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
                            const label = new Intl.DateTimeFormat('en-US', {
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            }).format(new Date(2020, 0, 1, h, m));

                            const isSelected =
                              dateTime.getHours() === h && dateTime.getMinutes() === m;

                            times.push(
                              <button
                                key={timeStr}
                                type="button"
                                onClick={() => handleTimeSelect(h, m)} // ✅ Closes on time select
                                disabled={isDisabled}
                                className={`text-sm py-2 px-2 rounded transition ${
                                  isDisabled
                                    ? "text-gray-600 bg-gray-900 cursor-not-allowed"
                                    : isSelected
                                    ? "bg-yellow-500 text-gray-900 font-semibold"
                                    : "text-white bg-gray-700 hover:bg-gray-600"
                                }`}
                              >
                                {label}
                              </button>
                            );
                          }
                        }
                        return times;
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Price Display */}
        {priceLoading && (
          <div className="flex items-center justify-center p-4 bg-gray-700/50 rounded-lg">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-yellow-500 mr-3"></div>
            <span className="text-gray-300">Calculating your fare...</span>
          </div>
        )}

        {priceError && (
          <div className="p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-300 text-center">
            {priceError}
          </div>
        )}

        {price && !priceLoading && (

          
          <div className="bg-gray-700/50 border border-gray-600 rounded-lg p-4">
             <div className="space-y-4">
    {/* Car Park Charge Notice */}
    {(locationFrom && locationTo) && (() => {
      const fromName = locations.find(loc => loc.id.toString() === locationFrom)?.name.toLowerCase() || '';
      
      const isAirportTrip = fromName.includes('airport') || fromName.includes('heathrow') ;
      
      return isAirportTrip && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <p className="block text-sm font-medium text-blue-800 mb-1">
            Car park charge will be added at cost to fare
          </p>
        </div>
      );
    })()}
    </div>
            <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 9a2 2 0 10-4 0v5a2 2 0 01-2 2h6m-6-4h4m8 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Fare Summary
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-300">Base Fare:</span>
                <span className="font-medium">£{price.basePrice.toFixed(2)}</span>
              </div>
              {price.surcharge > 0 && (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Surcharge:</span>
                    <span className="font-medium">+£{price.surcharge.toFixed(2)}</span>
                  </div>
                  <div className="text-xs text-gray-400 pl-2">
                    <svg className="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {price.surchargeDesc}
                  </div>
                </>
              )}
              <div className="border-t border-gray-600 pt-2 mt-2">
                <div className="flex justify-between font-bold text-lg">
                  <span>Total:</span>
                  <span className="text-yellow-400">£{price.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <button
          onClick={handleBookNow}
          disabled={!locationFrom || !locationTo || loading || priceLoading || !price}
          className={`w-full py-3 px-6 rounded-lg font-bold text-lg transition-all ${
            (!locationFrom || !locationTo || loading || priceLoading || !price)
              ? "bg-gray-600 text-gray-400 cursor-not-allowed"
              : "bg-yellow-500 hover:bg-yellow-600 text-gray-900 shadow-lg hover:shadow-yellow-500/30"
          }`}
        >
          {priceLoading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-900" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Calculating...
            </span>
          ) : (
            "Confirm Booking"
          )}
        </button>

        {/* Vehicle Info */}
        <div className="mt-4 pt-4 border-t border-gray-700">
          <div className="flex items-center">
            <div className="flex-shrink-0 h-12 w-12 rounded-lg bg-gray-700 flex items-center justify-center mr-3">
              <img 
                src={selectedVehicle.carImg} 
                alt={selectedVehicle.title} 
                className="h-10 object-contain"
              />
            </div>
            <div>
              <h4 className="font-medium text-white">{selectedVehicle.title}</h4>
              <div className="flex text-sm text-yellow-500 space-x-3 mt-1">
                <span className="flex items-center">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  {selectedVehicle.pass} passengers
                </span>
                <span className="flex items-center">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  {selectedVehicle.suit} suitcases
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Scrollbar for Time List */}
      <style jsx>{`
        .custom-time-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-time-scrollbar::-webkit-scrollbar-track {
          background: #374151;
          border-radius: 3px;
        }
        .custom-time-scrollbar::-webkit-scrollbar-thumb {
          background: #6b7280;
          border-radius: 3px;
        }
        .custom-time-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
    </div>
  );
};

// Helper function
function isSameDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export default Form;